import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "../../hooks/useTranslation";
import { LanguageSelector } from "./LanguageSelector";
export function WidgetHeader({ variant = "appointment", isMobile = false, onBack, primaryAction, compact = false, reserveBackSpace = true, rightContent, showLanguageSelector = true, showDivider = true, }) {
    const { t, language, changeLanguage } = useTranslation("appointment");
    const { t: tCommon } = useTranslation("common");
    const title = variant === "queue"
        ? t("widgetTitleQueue", "Join the Queue")
        : t("widgetTitle", "Book an Appointment");
    const subtitle = variant === "queue"
        ? t("widgetSubtitleQueue", "Select a doctor and secure your spot in the queue.")
        : t("widgetSubtitle", "Find the right specialist and schedule your visit in just a few clicks.");
    const selectLanguageLabel = t("selectLanguage", "Language");
    const backButtonSize = compact ? (isMobile ? 34 : 36) : isMobile ? 40 : 44;
    return (_jsx(Box, { sx: {
            px: compact ? (isMobile ? 1.5 : 2.25) : isMobile ? 2 : 3,
            pt: compact ? (isMobile ? 1 : 1.25) : isMobile ? 1.5 : 2,
            pb: compact ? (isMobile ? 0.9 : 1.15) : isMobile ? 1.25 : 1.75,
            borderBottom: showDivider ? 1 : 0,
            borderColor: "divider",
            bgcolor: "background.paper",
            flexShrink: 0,
        }, children: _jsxs(Stack, { direction: isMobile && rightContent ? "column" : "row", alignItems: isMobile && rightContent ? "stretch" : "center", justifyContent: "space-between", spacing: compact ? 1 : 1.5, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: compact ? 0.75 : 1, sx: { minWidth: 0, flex: 1 }, children: [onBack ? (_jsx(IconButton, { onClick: onBack, "aria-label": tCommon("back", "Back"), sx: {
                                flexShrink: 0,
                                width: backButtonSize,
                                height: backButtonSize,
                                p: isMobile ? 1.25 : 1.5,
                                ml: isMobile ? -0.5 : -0.75,
                            }, children: _jsx(ArrowBackIosNewIcon, { sx: { fontSize: isMobile ? 16 : 18, ml: 0.25 } }) })) : reserveBackSpace ? (_jsx(Box, { "aria-hidden": true, sx: {
                                flexShrink: 0,
                                width: backButtonSize,
                                height: backButtonSize,
                            } })) : null, _jsxs(Box, { sx: { minWidth: 0, flex: 1 }, children: [_jsx(Typography, { variant: isMobile || compact ? "subtitle1" : "h5", sx: {
                                        fontWeight: 700,
                                        letterSpacing: 0,
                                        lineHeight: compact ? 1.2 : 1.25,
                                        color: compact ? "text.primary" : "primary.main",
                                        fontSize: !compact && !isMobile ? { sm: 24, md: 29 } : undefined,
                                    }, children: title }), !isMobile && !compact && (_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 0.5, lineHeight: 1.5 }, children: subtitle }))] })] }), _jsxs(Stack, { direction: "row", alignItems: "center", flexWrap: "wrap", spacing: compact ? 0.75 : 1, useFlexGap: true, sx: {
                        flexShrink: 0,
                        gap: compact ? 0.75 : 1,
                        justifyContent: isMobile && rightContent ? "flex-start" : "flex-end",
                    }, children: [rightContent, primaryAction && (_jsx(Button, { variant: "contained", size: "small", onClick: primaryAction.onClick, disabled: primaryAction.disabled || primaryAction.loading, endIcon: primaryAction.loading ? undefined : (_jsx(ArrowForwardIcon, { sx: { fontSize: 14 } })), sx: {
                                borderRadius: "50px",
                                px: compact ? (isMobile ? 1.25 : 1.65) : isMobile ? 1.5 : 2,
                                height: compact ? (isMobile ? 30 : 34) : isMobile ? 32 : 36,
                                minWidth: primaryAction.loading ? 72 : undefined,
                                fontSize: compact || isMobile ? 12 : 13,
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                bgcolor: "secondary.main",
                                "&:hover": { bgcolor: "secondary.dark" },
                                "&.Mui-disabled": {
                                    bgcolor: "secondary.main",
                                    color: "secondary.contrastText",
                                    opacity: 0.55,
                                },
                                "&:focus-visible": {
                                    outline: "2px solid",
                                    outlineColor: "secondary.dark",
                                    outlineOffset: 2,
                                },
                            }, children: primaryAction.loading ? (_jsx(CircularProgress, { size: 14, color: "inherit" })) : (primaryAction.label) })), showLanguageSelector && (_jsx(LanguageSelector, { language: language, onChange: changeLanguage, label: selectLanguageLabel, isMobile: isMobile, compact: compact }))] })] }) }));
}
